import React, { useState, useRef } from 'react';
import { 
  DocumentArrowUpIcon, 
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { cn } from '../utils';

const ExcelImport = ({ isOpen, onClose, onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'text/csv',
        'application/csv',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      const isValidFile = validTypes.includes(file.type) || 
                         file.name.toLowerCase().endsWith('.csv') ||
                         file.name.toLowerCase().endsWith('.xlsx') ||
                         file.name.toLowerCase().endsWith('.xls');
      
      if (isValidFile) {
        setSelectedFile(file);
        setImportResults(null);
      } else {
        toast.error('Please select a valid CSV or Excel file (.csv, .xls, .xlsx)');
      }
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select an Excel file first');
      return;
    }

    setImporting(true);
    try {
      const response = await apiService.importCarsFromExcel(selectedFile);
      setImportResults(response.results);
      toast.success(response.message);
      
      if (onImportComplete && response.results.success > 0) {
        onImportComplete();
      }
    } catch (error) {
      toast.error(error.message);
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResults(null);
    setImporting(false);
    onClose();
  };

  const downloadTemplate = () => {
    // Create a sample Excel template with German column headers
    const templateData = [
      {
        'Firma': 'Beispiel GmbH',
        'Hersteller': 'BMW',
        'Model': 'X5',
        'Kennzeichen': 'B-MW-1234',
        'Fahrer': 'Max Mustermann',
        'Schlüsselnummer': '0005AAA',
        'Fahrgestellnummer': 'WBAFR1C50BC999999',
        'Datum': '2020-01-15',
        'Erstzulassung': '2020-01-15',
        'Reifengröße': '255/55R18'
      }
    ];

    // Convert to CSV for download
    const headers = Object.keys(templateData[0]);
    const csvContent = [
      headers.join(','),
      templateData.map(row => headers.map(header => row[header]).join(',')).join('\n')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'car_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white">Excel Import - Fahrzeuge</h2>
          <button
            onClick={handleClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Instructions */}
          <div className="mb-6 bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
            <div className="flex items-start">
              <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-blue-400 font-medium mb-2">Import Anweisungen</h3>
                <p className="text-blue-200 text-sm mb-3">
                  Laden Sie eine Excel-Datei (.xlsx oder .xls) mit den folgenden deutschen Spaltenüberschriften hoch:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-blue-300">
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Firma</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Hersteller</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Model</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Kennzeichen</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Fahrer</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Schlüsselnummer</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Fahrgestellnummer</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Datum</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Erstzulassung</div>
                  <div className="bg-blue-800/30 px-2 py-1 rounded">Reifengröße</div>
                </div>
                <div className="mt-3 flex items-center">
                  <button
                    onClick={downloadTemplate}
                    className="text-blue-400 hover:text-blue-300 text-sm underline flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Vorlage herunterladen
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-white mb-2">
              Excel-Datei auswählen
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-dark-600 rounded-lg p-6 text-center hover:border-primary-500 transition-colors"
                  disabled={importing}
                >
                  <DocumentArrowUpIcon className="h-12 w-12 text-dark-400 mx-auto mb-2" />
                  <p className="text-dark-300 mb-1">
                    {selectedFile ? selectedFile.name : 'Klicken Sie hier, um eine Excel-Datei auszuwählen'}
                  </p>
                  <p className="text-xs text-dark-400">
                    Unterstützte Formate: .xlsx, .xls (Max: 5MB)
                  </p>
                </button>
              </div>
              {selectedFile && (
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className={cn(
                    "px-6 py-3 bg-primary-600 text-white rounded-lg font-medium transition-colors",
                    importing 
                      ? "opacity-50 cursor-not-allowed" 
                      : "hover:bg-primary-700"
                  )}
                >
                  {importing ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Importiere...
                    </div>
                  ) : (
                    'Importieren'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="bg-dark-700 rounded-lg p-6 border border-dark-600">
              <h3 className="text-lg font-medium text-white mb-4">Import Ergebnisse</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{importResults.total}</div>
                  <div className="text-sm text-blue-300">Gesamt Zeilen</div>
                </div>
                <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400">{importResults.success}</div>
                  <div className="text-sm text-green-300">Erfolgreich</div>
                </div>
                <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-400">{importResults.failed}</div>
                  <div className="text-sm text-red-300">Fehlgeschlagen</div>
                </div>
              </div>

              {/* Error Details */}
              {importResults.errors && importResults.errors.length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-red-400 mb-3 flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    Fehler Details
                  </h4>
                  <div className="max-h-60 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <div key={index} className="bg-red-900/10 border border-red-800/30 rounded-lg p-3 mb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-red-400 font-medium">Zeile {error.row}</div>
                            <div className="text-red-300 text-sm">{error.error}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {importResults.success > 0 && (
                <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                  <div className="text-green-300">
                    {importResults.success} Fahrzeuge wurden erfolgreich importiert!
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-dark-600">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelImport;